import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from "@auth0/auth0-angular";
import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Observable, of, Subject, switchMap, take, takeUntil, timer } from 'rxjs';
import { EstimationService } from '../../core/estimation.service';
import { ProjectService } from "../../core/project.service";
import { RequisitionService } from '../../core/requisition.service';
import { UserService } from '../../core/user.service';
import { Estimation } from '../../shared/models/estimation.model';
import { DataCard } from "../../shared/models/information-dashboard.model";
import { ProjectMetrics, ProjectsList } from "../../shared/models/projects.model";
import { Requisition } from '../../shared/models/requisition.model';
import { RequisitionsChartMonthlyData, RequisitionsChartWeeklyData, RequisitionsPieChartData } from '../../shared/models/requisitions-pie-chart-data.model';
import { MyUser, User } from '../../shared/models/user.model';
import { EstimationsChartMonthlyData, EstimationsChartWeeklyData, EstimationsPieChartData } from '../../shared/models/estimations-pie-chart-data.model';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
  @Input() user: User = {} as User;
  requisitionsTabs: string[] = [];
  projectId: number | 'all' = 'all';
  project?: ProjectsList | 'all' = 'all';
  showAlert: boolean = true;
  requisitionCakeData: RequisitionsPieChartData = {} as RequisitionsPieChartData;
  requisitionChartMonthlyData1: RequisitionsChartMonthlyData[] = [];
  requisitionChartWeeklyData1: RequisitionsChartWeeklyData[] = [];
  infoRequisitionsCard: DataCard[] = [] as DataCard[];
  estimationCakeData: EstimationsPieChartData = {} as EstimationsPieChartData;
  estimationChartMonthlyData1: EstimationsChartMonthlyData[] = [];
  estimationChartWeeklyData1: EstimationsChartWeeklyData[] = [];
  infoEstimationsCard: DataCard[] = [] as DataCard[];

  requisitionTableData: Requisition[] = [];
  public totalRequisitions: number = 30;
  public totalBought: number = 20;
  projectCardInfo: Partial<ProjectMetrics> = {} as Partial<ProjectMetrics>;

  public estimationTableData: Estimation[] = [];
  public totalEstimations: number = 30;

  private destroy: Subject<void> = new Subject<void>();
  private projectIdSubject: BehaviorSubject<number | 'all' > = new BehaviorSubject<number | 'all'>('all');
  userPermissions?: {
    canViewAmounts?: boolean;
    isAdmin?: boolean;
    requisitions: boolean;
    estimations: boolean;
  }

  constructor(private router: Router,
              private requisitionService: RequisitionService,
              private projectService: ProjectService,
              private createMessage: NzMessageService,
              private estimationService: EstimationService,
              private userService: UserService,
              public auth: AuthService) {
  }

  ngOnInit(): void {
    this.requisitionsTabs = this.requisitionService.requisitionTabs;
    this.projectIdSubject.pipe(takeUntil(this.destroy), switchMap(() => {
      return this.userService.getDataUser();
    }),
    switchMap((userData: MyUser) => {
        this.userPermissions = {
          canViewAmounts: userData.permissions?.canViewAmounts ?? false,
          isAdmin: userData.permissions?.isAdmin ?? false,
          requisitions: userData.user.accessModules?.requisitions ?? false,
          estimations: userData.user.accessModules?.workProgressStatement ?? false,
        };

        if (this.userPermissions.canViewAmounts) {
          return this.projectService.getProjectMetrics(this.projectId);
        } else {
          return of([] as ProjectMetrics[]);
        }
      }),
      switchMap((metrics: ProjectMetrics[]) => {
        if (metrics.length > 0) {
          this.projectCardInfo = {
            Budget: metrics[0].Budget,
            ExecutionCost: metrics[0].ExecutionCost
          }

          this.requisitionCakeData = metrics[0].RequisitionsData;
          this.requisitionChartMonthlyData1 = metrics[0].MonthlyRequisition;
          this.requisitionChartWeeklyData1 = metrics[0].WeeklyRequisition;
          this.buildDataRequisitions(metrics[0].RequisitionsData);

          this.estimationCakeData = metrics[0].WorkPerformedValue;
          this.estimationChartMonthlyData1 = metrics[0].MonthlyWorkPerformedValues;
          this.estimationChartWeeklyData1 = metrics[0].WeeklyWorkPerformedValues;
          this.buildDataEstimations(metrics[0].WorkPerformedValue);
        }
        if (this.userPermissions?.requisitions) {
          return this.requisitionService.getLatestToAuthorizeRequisitions(this.projectId, 0);
        } else {
          return of([] as Requisition[]);
        }
      }),
      switchMap((requisitionsData: Requisition[]) => {
        this.requisitionTableData = requisitionsData || [];
        this.totalRequisitions = requisitionsData.length;

        if (this.userPermissions?.estimations) {
          return this.estimationService.getLatestToAuthorizeEstimations(this.projectId, 0);
        } else {
          return of([] as Estimation[]);
        }
      }))
    .subscribe({
      next: (estimationsData: Estimation[]) => {
        estimationsData = estimationsData as Estimation[];
        this.estimationTableData = estimationsData || [];
        this.totalEstimations = estimationsData.length;
      },
      error: (error) => {
        this.createMessage.error('😕 Ocurrió un problema al cargar la información financiera del proyecto. Intenta nuevamente en unos momentos o contacta a nuestro equipo ACYST si el problema persiste.')
        console.error('No se pudo obtener los datos de los proyectos: ', error.message);
      }
    });

    timer(5000)
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.showAlert = false);
  }

  public projectSelected(project: ProjectsList | 'all'): void {
    this.project = project;
    this.projectId = project === 'all' ? this.projectId : project.projectId;
    this.projectIdSubject.next(this.projectId);
  }

  goToMyRequisitions(): void {
    this.router.navigate([`requisiciones/${this.projectId}`]);
  }

  goToMyEstimations(): void {
    this.router.navigate([`estimaciones/${this.projectId}`]);
  }

  onHighlightRequisition(event: { requisition: Requisition, tableIndex: number }) {
    this.requisitionService
      .updateRequisition({
        requisitionId: event.requisition.requisitionId,
        requisitionIsHighlighted: false,
        field: 'requisitionIsHighlighted'
      })
      .pipe(take(1))
      .subscribe((data) => {
        if ('requisitionId' in data) {
          this.requisitionTableData[event.tableIndex].requisitionIsHighlighted = false;
          this.createMessage.info(`La requisición fue eliminada de favoritos.`);
        }
      });
  }

  onHighlightEstimation(data: Estimation, isHighlighted: boolean, tableIndex: number) {
    // this.estimationService
    //   .updateEstimation({
    //     estimationId: data.estimationId,
    //     estimationIsHighlighted: isHighlighted,
    //   })
    //   .pipe(take(1))
    //   .subscribe((data) => {
    //     if (data) {
    //       this.estimationTableData[tableIndex].estimationIsHighlighted =
    //         isHighlighted;
    //     }
    //   });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  private buildDataRequisitions(data: RequisitionsPieChartData): void {
    this.infoRequisitionsCard = [
      {
        title: "Total Requisitado",
        value: data.totalAmount
      },
      {
        title: "Requisitado por Autorizar",
        value: data.totalPendingAuthorization
      },
      {
        title: "Monto Autorizado",
        value: data.totalAuthorized
      },
      {
        title: "Comprado",
        value: data.totalPurchased
      }
      ]
  }

  private buildDataEstimations(data: EstimationsPieChartData): void {
    this.infoEstimationsCard = [
      {
        title: "Total Estimado",
        value: data.totalWorkPerformedValue
      },
      {
        title: "Estimado por autorizar",
        value: data.totalPendingAuthorizationWorkPerformedValue
      },
      {
        title: "Monto autorizado",
        value: data.totalAuthorizedWorkPerformedValue
      },
      ]
  }
}
